import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import DownloadRequestsTray from './DownloadRequestsTray';
import { NAV_SECTIONS } from '../lib/navConfig';
import { UserCircle, LogOut, Menu, X, ChevronDown, PanelLeftClose, PanelLeftOpen } from 'lucide-react';

const LOGO = 'https://customer-assets.emergentagent.com/job_doc-insights-24/artifacts/8eif3nnf_Logo.png';

const ROLE_BADGE = {
  super_admin: 'bg-purple-100 text-purple-700',
  finance_admin: 'bg-sky-100 text-sky-700',
  pre_sales: 'bg-emerald-100 text-emerald-700',
  sales: 'bg-amber-100 text-amber-800',
  operations: 'bg-slate-100 text-slate-700',
  marketing: 'bg-pink-100 text-pink-700',
  doctor: 'bg-teal-100 text-teal-700',
  pending: 'bg-rose-100 text-rose-700',
};
const formatRole = (role) => role?.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase()) || 'Unknown';

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(() => localStorage.getItem('ops_sidebar_open_v2') !== '0');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  useEffect(() => { localStorage.setItem('ops_sidebar_open_v2', sidebarOpen ? '1' : '0'); }, [sidebarOpen]);

  const filteredSections = NAV_SECTIONS
    .map((section) => ({ ...section, items: section.items.filter((it) => it.roles?.includes(user?.role)) }))
    .filter((section) => section.items.length > 0);

  const [sectionOpenState, setSectionOpenState] = useState(() => {
    try {
      const saved = localStorage.getItem('ops_sidebar_sections_v1');
      if (saved) return JSON.parse(saved);
    } catch (e) { /* ignore */ }
    return NAV_SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: s.defaultOpen }), {});
  });
  useEffect(() => {
    try { localStorage.setItem('ops_sidebar_sections_v1', JSON.stringify(sectionOpenState)); } catch (e) { /* ignore */ }
  }, [sectionOpenState]);
  const toggleSection = (id) => setSectionOpenState((prev) => ({ ...prev, [id]: !prev[id] }));

  const currentPage = filteredSections.flatMap((s) => s.items).find((it) => location.pathname.startsWith(it.href));
  const expanded = sidebarOpen || mobileMenuOpen;

  if (user?.status === 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="card p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserCircle className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold mb-2">Account Pending Approval</h2>
          <p className="text-muted-foreground mb-6">Your account is pending approval from an administrator.</p>
          <button onClick={logout} className="btn-secondary">Sign Out</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {mobileMenuOpen && <div className="fixed inset-0 bg-slate-900/60 z-30 lg:hidden" onClick={() => setMobileMenuOpen(false)} />}

      <aside
        data-testid="sidebar"
        className={`fixed top-0 left-0 z-40 h-screen bg-[hsl(var(--rail))] text-slate-100 transition-[width,transform] duration-300 flex flex-col
          ${sidebarOpen ? 'w-60' : 'w-16'} ${mobileMenuOpen ? 'translate-x-0 w-60' : '-translate-x-full'} lg:translate-x-0`}
      >
        <div className={`h-16 flex items-center border-b border-white/10 ${expanded ? 'px-4 justify-between' : 'justify-center'}`}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 shrink-0 rounded-lg bg-white p-1 flex items-center justify-center">
              <img src={LOGO} alt="Soul Sanskar" className="h-full w-full object-contain" />
            </div>
            {expanded && (
              <div className="leading-tight min-w-0">
                <p className="font-display font-bold text-sm text-white truncate">Soul Sanskar</p>
                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400">OPS Panel</p>
              </div>
            )}
          </div>
          {mobileMenuOpen && (
            <button onClick={() => setMobileMenuOpen(false)} className="p-1.5 rounded-md hover:bg-white/10 text-slate-300 lg:hidden" aria-label="Close menu">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <nav className={`flex-1 overflow-y-auto py-3 space-y-3 ${expanded ? 'px-3' : 'px-2'}`} data-testid="sidebar-nav">
          {filteredSections.map((section) => {
            const SectionIcon = section.icon;
            const isOpen = sectionOpenState[section.id] || !expanded;
            return (
              <div key={section.id} data-testid={`sidebar-section-${section.id}`}>
                {expanded ? (
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 hover:text-slate-200 transition-colors"
                    data-testid={`sidebar-section-toggle-${section.id}`}
                  >
                    <span>{section.label}</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                ) : (
                  <div className="flex justify-center py-1 text-slate-500" title={section.label} data-testid={`sidebar-section-toggle-${section.id}`}>
                    <SectionIcon className="w-3.5 h-3.5" />
                  </div>
                )}
                {isOpen && (
                  <div className="mt-0.5 space-y-0.5">
                    {section.items.map((item) => {
                      const ItemIcon = item.icon || section.icon;
                      return (
                        <NavLink
                          key={item.name}
                          to={item.href}
                          onClick={() => setMobileMenuOpen(false)}
                          title={item.name}
                          className={({ isActive }) => `sidebar-item text-sm ${expanded ? '' : 'justify-center px-0'} ${isActive ? 'active' : ''}`}
                          data-testid={`sidebar-nav-${section.id}-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <ItemIcon className="w-4 h-4 flex-shrink-0" />
                          {expanded && <span className="truncate">{item.name}</span>}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          <button onClick={logout} className="sidebar-item w-full text-rose-300 hover:bg-rose-500/10 lg:hidden mt-4" data-testid="sidebar-mobile-logout-btn">
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <span>Sign Out</span>
          </button>
        </nav>

        <div className="border-t border-white/10 p-2 hidden lg:block">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="sidebar-item w-full justify-center text-slate-400"
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            data-testid="sidebar-collapse-btn"
          >
            {sidebarOpen ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeftOpen className="w-4 h-4" />}
          </button>
        </div>
      </aside>

      <div className={`transition-[margin] duration-300 ${sidebarOpen ? 'lg:ml-60' : 'lg:ml-16'} ml-0 min-h-screen overflow-x-hidden`}>
        <header className="h-16 sticky top-0 z-20 flex items-center gap-3 px-4 lg:px-6 border-b border-border bg-white/85 backdrop-blur-md" data-testid="topbar">
          <button onClick={() => setMobileMenuOpen(true)} className="btn-ghost p-2 lg:hidden" aria-label="Open menu" data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground hidden sm:block">
              {filteredSections.find((s) => s.items.some((it) => location.pathname.startsWith(it.href)))?.label || 'Workspace'}
            </p>
            <p className="font-display text-sm font-semibold text-foreground truncate" data-testid="topbar-page-name">{currentPage?.name || 'OPS Panel'}</p>
          </div>

          {user?.role !== 'super_admin' && <DownloadRequestsTray />}
          <NotificationBell />

          <div className="relative">
            <button onClick={() => setUserMenuOpen(!userMenuOpen)} className="flex items-center gap-2 lg:gap-3 p-1.5 rounded-lg hover:bg-muted transition-colors" data-testid="user-menu-btn">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-full ring-2 ring-border" />
              ) : (
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-primary font-semibold text-sm">{user?.full_name?.charAt(0) || 'U'}</span>
                </div>
              )}
              <div className="hidden lg:block text-left leading-tight">
                <p className="text-sm font-medium text-foreground">{user?.full_name}</p>
                <span className={`text-[10px] px-2 py-0.5 rounded-full inline-block font-semibold ${ROLE_BADGE[user?.role] || 'bg-slate-100 text-slate-700'}`}>{formatRole(user?.role)}</span>
              </div>
              <ChevronDown className="w-4 h-4 text-slate-400 hidden lg:block" />
            </button>
            {userMenuOpen && (
              <div className="absolute right-0 mt-2 w-60 card py-2 z-50 shadow-lg animate-fade-in">
                <div className="px-4 py-2 border-b border-border/70">
                  <p className="text-sm font-medium text-foreground">{user?.full_name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <button onClick={() => { setUserMenuOpen(false); logout(); }} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-rose-600 hover:bg-rose-50" data-testid="logout-btn">
                  <LogOut className="w-4 h-4" /> Sign Out
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
